import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getManagementTenantPreferencesOptions,
  getTenantOptions,
  listTenantUsersOptions,
  type User,
} from "@harmonyso/tenants-sdk";
import { impersonateUserMutation } from "@harmonyso/auth-sdk";
import type { ImpersonationResponse } from "@harmonyso/auth-sdk";
import { AlertTriangle, ArrowLeft, Building2 } from "lucide-react";
import { useState, useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TenantHeader } from "@/components/tenants/tenant-header";
import { TenantInfoCards } from "@/components/tenants/tenant-info-cards";
import { TenantUsersSection } from "@/components/tenants/tenant-users-section";
import { TenantIntegrationsSection } from "@/components/tenants/tenant-integrations-section";
import { TenantAdminsSection } from "@/components/tenants/tenant-admins-section";
import { JwtDisplayDialog } from "@/components/tenants/jwt-display-dialog";
import { useBillingConfig } from "@/hooks/use-billing";
import { useTenantCell } from "@/hooks/use-cell-tenants";
import {
  CellFailuresAlert,
  CellSignInButton,
} from "@/components/cells/cell-failures-alert";
import { cellRequestOptions, isRemoteCellId } from "@/lib/cells/cells";
import { cellAwareRetry, isCellSignInError } from "@/lib/cells/cell-session";

const searchSchema = z.object({
  cell: z.string().optional(),
});

export const Route = createFileRoute("/tenants/$tenantId")({
  component: TenantDetailPage,
  validateSearch: zodValidator(searchSchema),
});

function TenantDetailPage() {
  const { tenantId } = Route.useParams();
  const { cell: cellFromSearch } = Route.useSearch();
  const { cellId, failures: cellLookupFailures } = useTenantCell(
    tenantId,
    cellFromSearch,
  );
  const isRemoteTenant = isRemoteCellId(cellId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [jwtResult, setJwtResult] = useState<ImpersonationResponse | null>(
    null,
  );
  const [showJwtDialog, setShowJwtDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [allUsersError, setAllUsersError] = useState<Error | null>(null);
  const [totalUsersCount, setTotalUsersCount] = useState(0);

  const {
    data: tenant,
    isLoading: tenantLoading,
    error,
  } = useQuery({
    ...getTenantOptions({
      ...cellRequestOptions(cellId),
      path: { tenant_id: tenantId },
    }),
    enabled: cellId !== undefined,
    retry: cellAwareRetry,
    select: (data) => ({ ...data, deployment_cell: cellId }),
  });
  const isLoading = tenantLoading || cellId === undefined;
  const { data: preferences, isLoading: preferencesLoading } = useQuery({
    ...getManagementTenantPreferencesOptions({
      ...cellRequestOptions(cellId),
      path: { tenant_id: tenantId },
    }),
    enabled: cellId !== undefined,
    retry: cellAwareRetry,
  });
  const {
    data: billingConfig,
    isLoading: billingConfigLoading,
    error: billingConfigError,
  } = useBillingConfig(
    cellId === undefined || isRemoteTenant ? undefined : tenantId,
  );

  // Fetch all users in batches
  useEffect(() => {
    if (!tenant) return;

    const fetchAllUsers = async () => {
      setAllUsersLoading(true);
      setAllUsersError(null);

      try {
        const batchSize = 100;
        let offset = 0;
        let allUsersList: User[] = [];
        let totalCount = 0;

        // First batch to get total count
        const firstBatchOptions = listTenantUsersOptions({
          ...cellRequestOptions(cellId),
          path: { tenant_id: tenantId },
          query: { limit: batchSize, offset: 0 },
        });

        const firstBatch = await queryClient.fetchQuery(firstBatchOptions);

        allUsersList = firstBatch.items;
        totalCount = firstBatch.total_count || 0;
        setTotalUsersCount(totalCount);

        // If there are more users, fetch them in batches
        if (totalCount > batchSize) {
          const batches = Math.ceil((totalCount - batchSize) / batchSize);

          for (let i = 1; i <= batches; i++) {
            offset = i * batchSize;
            const batchOptions = listTenantUsersOptions({
              ...cellRequestOptions(cellId),
              path: { tenant_id: tenantId },
              query: { limit: batchSize, offset },
            });

            const batch = await queryClient.fetchQuery(batchOptions);

            allUsersList = [...allUsersList, ...batch.items];
          }
        }

        setAllUsers(allUsersList);
      } catch (error) {
        setAllUsersError(error as Error);
      } finally {
        setAllUsersLoading(false);
      }
    };

    void fetchAllUsers();
  }, [tenant, tenantId, cellId, queryClient]);

  // Create usersData object that matches the expected format
  const usersData = {
    items: allUsers,
    total_count: totalUsersCount,
  };

  const impersonateMutation = useMutation({
    ...impersonateUserMutation(),
    onSuccess: (data) => {
      setJwtResult(data);
      setShowJwtDialog(true);
    },
    onError: (error) => {
      console.error("Impersonation failed:", error);
      // TODO: Show error toast
    },
  });

  const handleImpersonate = (tenantId: string, loginId: string) => {
    impersonateMutation.mutate({
      body: {
        tenant_id: tenantId,
        login_id: loginId,
      },
    });
  };

  // Reset to first page when search query changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
        <div className="flex shrink-0 items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({ to: "/tenants" });
            }}
            className="hover:cursor-pointer"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tenants
          </Button>
        </div>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
          <CellFailuresAlert failures={cellLookupFailures} />
          <Card>
            <CardContent className="pt-6">
              <div className="py-12 text-center">
                <Building2 className="mx-auto mb-4 h-12 w-12 text-red-500" />
                <h3 className="mb-2 text-lg font-semibold">Tenant Not Found</h3>
                <p className="text-muted-foreground">
                  {error?.message ??
                    `Could not find tenant with ID: ${tenantId}`}
                </p>
                {cellId && isRemoteTenant && isCellSignInError(error) && (
                  <div className="mt-4">
                    <CellSignInButton cellId={cellId} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
        <div className="shrink-0">
          <TenantHeader tenant={tenant} billingConfig={billingConfig} />
        </div>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
          <TenantInfoCards
            tenant={tenant}
            tenantId={isRemoteTenant ? undefined : tenantId}
            billingConfig={billingConfig}
            billingConfigLoading={billingConfigLoading}
            billingConfigError={billingConfigError}
            trial={preferences?.trial}
            trialLoading={preferencesLoading}
          />

          {isRemoteTenant ? (
            <div
              role="status"
              className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20"
            >
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400"
              />
              <div className="text-sm text-yellow-700 dark:text-yellow-300">
                This tenant is in {cellId}. Billing, integrations, archiving and
                impersonation aren't available for it here yet.
              </div>
            </div>
          ) : (
            <>
              <TenantAdminsSection
                tenantId={tenantId}
                tenantName={tenant.company}
              />
              <TenantIntegrationsSection tenantId={tenantId} />
            </>
          )}

          <TenantUsersSection
            usersData={usersData}
            usersLoading={allUsersLoading}
            usersError={allUsersError}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            tenantId={tenantId}
            onImpersonate={isRemoteTenant ? undefined : handleImpersonate}
            isImpersonating={impersonateMutation.isPending}
          />

          <JwtDisplayDialog
            isOpen={showJwtDialog}
            onClose={() => {
              setShowJwtDialog(false);
            }}
            impersonationResult={jwtResult}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
